<template>
  <div class="flex flex-col gap-32">
    <div v-if="product" class="bg-surface-default-low">
      <SeoBreadcrumb :breads class="mb-32" />
      <SeoHero
        :imageUrl="product.image"
        :price="product.price"
        :productName="product.model"
        @navigate-to-funnel="handleNavigateToFunnel"
      />
    </div>
    <SeoReassurance />
    <SeoSearchBar @item-selected="handleItemSelected" />
    <SeoCategories :categories @category-click="handleCategoryClick" />
    <div class="bg-surface-default-low">
      <SeoProductLinks
        :sections="[seoSections]"
        @link-click="handleLinkClick"
      />
      <SeoCategoriesTags :categories />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createError, navigateTo } from '#imports'

import { useRouteParams } from '@backmarket/nuxt-layer-base/composables/useRouteParams'
import type { BuybackProduct } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import SeoProductLinks from '@backmarket/nuxt-layer-buyback/app/scopes/offer/components/SeoProductLinks/SeoProductLinks.vue'
import { useBuybackCategories } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackCategories/useBuybackCategories'
import { useSeoProductsByCategory } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useSeoProductsByCategory/useSeoProductsByCategory'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import SeoBreadcrumb from '~/scopes/buyback/components/SeoBreadcrumb/SeoBreadcrumb.vue'
import SeoCategories from '~/scopes/buyback/components/SeoCategories/SeoCategories.vue'
import SeoCategoriesTags from '~/scopes/buyback/components/SeoCategoriesTags/SeoCategoriesTags.vue'
import SeoHero from '~/scopes/buyback/components/SeoHero/SeoHero.vue'
import SeoReassurance from '~/scopes/buyback/components/SeoReassurance/SeoReassurance.vue'
import SeoSearchBar from '~/scopes/buyback/components/SeoSearchBar/SeoSearchBar.vue'
import { useSeoBreadcrumb } from '~/scopes/buyback/composables/useSeoBreadcrumb'
import { useSeoProductById } from '~/scopes/buyback/composables/useSeoProductById'
import { useSeoProductPageHead } from '~/scopes/buyback/composables/useSeoProductPageHead'
import { FUNNEL_ERROR_MESSAGE, ROUTE_NAMES } from '~/scopes/buyback/constants'

import translations from './ProductPage.translations'

const { id } = useRouteParams()
const { product, error } = await useSeoProductById(id)
const i18n = useI18n()
const { trackClick, trackBlockImpression } = useTracking()
const logger = useLogger()

if (!product.value) {
  logger.error(FUNNEL_ERROR_MESSAGE.PRODUCT_NOT_FOUND, {
    error: error.value,
    info: id,
    owners: ['bot-squad-circularity-offer-front'],
  })

  throw createError({
    status: 404,
    statusText: i18n(translations.productNotFound),
  })
}

await useSeoProductPageHead(product.value)

const { seoSections } = await useSeoProductsByCategory(
  product.value.category,
  product.value.category === 'smartphone' && product.value.brand === 'Apple'
    ? product.value.brand
    : undefined,
)

const { breads } = useSeoBreadcrumb(
  product.value.category,
  product.value.brand,
  product.value.model,
)

const { categories, getLinkByCategory } = useBuybackCategories()

trackBlockImpression({
  productCategory: product.value.category,
  productBrand: product.value.brand,
  productModel: product.value.model,
  productPrice: product.value.price?.amount ?? '',
})

function createFunnelLink(item: BuybackProduct) {
  const query = {
    brand: item.brand,
    model: item.model,
    model_family: item.productLine,
    nextStep: 'false',
  }

  const linkParams = {
    name: ROUTE_NAMES.DEVICE_ASSESSMENT,
    type: 'internal',
    params: { slug: item.category, id: item.category },
    query: removeEmptyValuesInObject(query),
  }

  return toNuxtLink(linkParams)
}

async function handleItemSelected(item: BuybackProduct) {
  await navigateTo(createFunnelLink(item))
}

async function handleCategoryClick(categoryName: string) {
  await navigateTo(getLinkByCategory(categoryName))
}
function handleLinkClick(productClicked: { label: string; position: number }) {
  trackClick({
    zone: 'buyback_master-pp',
    name: 'footer_link_click',
    value: {
      productName: productClicked.label,
      position: productClicked.position,
    },
  })
}

async function handleNavigateToFunnel() {
  if (!product.value) {
    return
  }

  await navigateTo(createFunnelLink(product.value))

  trackClick({
    zone: 'buyback_master-pp',
    name: 'See Trade In value',
    value: {
      productCategory: product.value.category,
      productBrand: product.value.brand,
      productModel: product.value.model,
      productPrice: product.value.price?.amount ?? '',
    },
  })
}
</script>
